import { StoreValues } from 'types/store';
import { readThemeAppEmbedEvidence } from 'utils/themeAppEmbed';
import type { InstallationStatus } from 'types/installationVerification';
import { readInstallationStatus } from './installationVerification';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Scope only a loaded, connected store with its canonical Shopify domain. */
export const currentInstallationScope = (storeId: string, store: StoreValues): string | null => {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(storeId) ||
    store.id !== storeId || store.uninstalledAt || typeof store.store !== 'string' ||
    !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/i.test(store.store)) return null;
  return `${storeId}:${store.store.toLowerCase()}`;
};

export const configuredInstallationBoolean = (value: unknown): boolean | null =>
  isRecord(value) && typeof value.enabled === 'boolean' ? value.enabled : null;

/** MAIN embed configuration proves a theme method, never cart layout/runtime. */
export const currentThemeInstallationMethod = (
  payload: unknown, storeId: string, shopDomain: string,
): 'theme' | null => {
  if (!isRecord(payload) || !isRecord(payload.readiness)) return null;
  const readiness = payload.readiness;
  if (readiness.runtimeStatus !== 'unverified' || readiness.reasonCode !== 'embed_enabled' ||
    typeof readiness.publishedThemeId !== 'string' || !/^[1-9]\d*$/.test(readiness.publishedThemeId)) return null;
  return readThemeAppEmbedEvidence(payload, storeId, shopDomain).status === 'enabled' ? 'theme' : null;
};

const own = (value: Record<string, unknown>, key: string): unknown =>
  Object.prototype.hasOwnProperty.call(value, key) ? value[key] : undefined;

/** The same authenticated status model is served to Home and Installation. */
export const readCurrentInstallationStatus = (payload: unknown, storeId: string, domain?: string): {
  status?: InstallationStatus; unavailable: boolean;
} => {
  if (!isRecord(payload) || !Object.prototype.hasOwnProperty.call(payload, 'installationStatus')) return { unavailable: false };
  try {
    if (!domain) throw new Error('Store identity unavailable');
    const status = readInstallationStatus(payload.installationStatus, storeId, domain);
    const readiness = payload.readiness;
    if (status.themeId && isRecord(readiness) && typeof readiness.publishedThemeId === 'string'
      && status.themeId !== readiness.publishedThemeId) throw new Error('Published theme changed during the check');
    return { status, unavailable: false };
  } catch { return { unavailable: true }; }
};

export const hasCurrentInstallationConfiguration = (payload: unknown): boolean =>
  isRecord(payload) && Object.prototype.hasOwnProperty.call(payload, 'currentConfiguration');

type CurrentConfigurationFacts = {
  checkoutPlus: boolean | null;
  addToCart: boolean | null;
  installationMethod: 'theme' | null;
};

const booleanFact = (value: unknown): value is { value: boolean | null } => {
  if (!isRecord(value)) return false;
  const reason = own(value, 'reason');
  return typeof own(value, 'value') === 'boolean'
    ? own(value, 'source') === 'store_configuration' && own(value, 'reason') === 'configured'
    : own(value, 'value') === null && own(value, 'source') === null &&
      typeof reason === 'string' && ['configuration_missing', 'configuration_invalid'].includes(reason);
};

const methodFact = (value: unknown): value is { value: 'theme' | null } => {
  if (!isRecord(value)) return false;
  const reason = own(value, 'reason');
  return own(value, 'value') === 'theme'
    ? own(value, 'source') === 'published_theme_embed' && own(value, 'reason') === 'theme_embed_detected'
    : own(value, 'value') === null && own(value, 'source') === null && typeof reason === 'string' && [
      'current_identity_missing', 'current_identity_invalid', 'embed_evidence_missing',
      'embed_evidence_invalid', 'embed_scope_mismatch', 'embed_not_enabled',
    ].includes(reason);
};

const unsupportedFact = (value: unknown, reason: string): boolean =>
  isRecord(value) && own(value, 'value') === null && own(value, 'source') === null && own(value, 'reason') === reason;

/** Read only supported companion facts; a present invalid model never supplies legacy fallbacks. */
export const readCurrentInstallationConfiguration = (
  payload: unknown, storeId: string, shopDomain: string,
): CurrentConfigurationFacts | null => {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(storeId) ||
    !/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(shopDomain) || !isRecord(payload)) return null;
  const config = own(payload, 'currentConfiguration');
  if (!isRecord(config) || own(config, 'schemaVersion') !== 1 || own(config, 'storeId') !== storeId ||
    own(config, 'shopDomain') !== shopDomain || own(config, 'configurationRevision') !== null ||
    own(config, 'runtimeStatus') !== 'unverified') return null;
  const scope = own(config, 'scope');
  const fields = own(config, 'fields');
  if (!isRecord(scope) || own(scope, 'status') !== 'ready' || own(scope, 'reason') !== 'store_ready' ||
    !isRecord(fields)) return null;
  const checkoutPlus = own(fields, 'checkoutPlusEnabled');
  const addToCart = own(fields, 'addToCartPlusEnabled');
  const method = own(fields, 'installationMethod');
  if (!booleanFact(checkoutPlus) || !booleanFact(addToCart) || !methodFact(method) ||
    !unsupportedFact(own(fields, 'payer'), 'authoritative_funding_policy_missing') ||
    !['cartExperience', 'namedLayout', 'placement', 'design'].every((key) =>
      unsupportedFact(own(fields, key), 'active_installation_record_missing'))) return null;

  const readiness = own(payload, 'readiness');
  const coherentPrimary = isRecord(readiness) && own(payload, 'shipaid-widget') === true &&
    own(readiness, 'schemaVersion') === 1 && own(readiness, 'storeId') === storeId &&
    own(readiness, 'shopDomain') === shopDomain && own(readiness, 'embedHandle') === 'shipaid-widget' &&
    own(readiness, 'embedStatus') === 'enabled' && own(readiness, 'reasonCode') === 'embed_enabled' &&
    own(readiness, 'runtimeStatus') === 'unverified' && own(readiness, 'themeSettingsError') === null &&
    typeof own(readiness, 'installedAppClientId') === 'string' &&
    /^[A-Za-z0-9]{1,64}$/.test(own(readiness, 'installedAppClientId') as string) &&
    typeof own(readiness, 'publishedThemeId') === 'string' &&
    /^[1-9]\d*$/.test(own(readiness, 'publishedThemeId') as string);
  return { checkoutPlus: checkoutPlus.value, addToCart: addToCart.value,
    installationMethod: method.value === 'theme' && coherentPrimary ? 'theme' : null };
};
